/**
 * Resolve Dialog Component
 *
 * Allows manual resolution of production issues.
 *
 * Special case: a PRINT_FILE_ISSUE renders a repair flow instead of the
 * manual/hold radio. The server owns the matrix of files the item needs (one
 * per side × printer format); the operator attaches each missing one, the
 * browser mints a presigned URL, PUTs to the active print cloud, and the
 * server re-verifies and resolves once EVERY required file is present. A
 * metadata issue puts the design-data form first and reveals the same
 * uploader beneath it. Every other issue type keeps the manual/hold flow.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, CheckCircle, ExternalLink, Pause, Loader2, RefreshCw, Upload } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  SUPPLY_MAPPING_INVALID_PRINT_FILE_REASON,
  WRONG_DATA_PRINT_FILE_REASON_SET,
} from '@threadheads/shared';
import { useResolveIssue } from '../hooks/useProductionIssues';
import { usePrintFileIssueRecheck } from '../hooks/usePrintFileIssueRecheck';
import { trpc } from '@/lib/trpc';
import { getUserMessage } from '@/lib/trpc-error-utils';
import { getShopifyAdminProductUrl } from '@/lib/shopify-urls';
import { useOrganization } from '@/contexts/OrganizationContext';
import { toast } from 'sonner';
import type { IssueProductRoute } from '../hooks/usePlatenVariantRoute';
import { PrintFileSelfHealForm } from './PrintFileSelfHealForm';
import { PrintDataForm } from './PrintDataForm';
import { FixDesignDataForm } from './FixDesignDataForm';
import { WrongDataUploadForm } from './WrongDataUploadForm';
import { ArtworkUrlSection } from './ArtworkUrlSection';
import { snapshotFromFields } from '../bridgeFetchState';
import { DESIGN_DATA_DIALOG_CLASS } from '@/lib/dialog-classes';

// Wrong-DATA print-file reasons — the artwork exists but the lookup row points
// at the wrong design; resolved by correcting the design data (or a corrected
// upload), not by upload/self-heal probing. Canonical set shared with the
// backend gates (BLU-651).
const WRONG_DATA_REASONS = WRONG_DATA_PRINT_FILE_REASON_SET;

// One width for every design-data step (variant set-up, print data, fix design
// data, upload after save) so the dialog never jumps between steps. 768px is the
// smallest step where the three-button action row fits on one line in en; below
// it the footer wraps instead of scrolling sideways (DESIGN.md §22). The class
// lives in `lib/dialog-classes` so every data dialog shares it.

interface ResolveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  issueId: string;
  /** The issue's location — scopes the live bridge-progress stream (BLU-880). */
  locationId?: string | null;
  orderName?: string;
  issueType?: string;
  /** Issue metadata — used to detect the file_missing self-heal case. */
  metadata?: Record<string, unknown>;
  /**
   * BLU-880: the row's live bridge-fetch projection (the DAO's typed
   * `bridgeFetchState/At/FailReason`). Seeds the stepper when the dialog is
   * opened on a run already in flight, so reopening never loses progress.
   */
  bridgeFetchState?: string | null;
  bridgeFetchStateAt?: string | null;
  bridgeFetchFailReason?: string | null;
  /** Design-bearing SKU (blocked_sku) — shown as context in the print-data form. */
  blockedSku?: string | null;
  /** order_items.product_id: the Product updates link for a platen variant size opens this product. */
  shopifyProductId?: number | string | null;
  /** The issue row's org: decides who is asked to upload a platen variant file. */
  issueOrganizationId?: string | null;
  /**
   * BLU-1287: who acts on this issue's product in its store, computed by the
   * issue card from values it already loaded (issueProductRoute). Absent:
   * ask an admin (never a link the dialog cannot vouch for).
   */
  productRoute?: IssueProductRoute;
  onSuccess?: () => void;
}

/** BLU-1287: after a save that added a back, where Continue goes and the product to link. */
interface VariantSetup {
  next: 'resolved' | 'upload';
  productId: number | null;
}

/**
 * BLU-1287: the variant set-up step's content. back_design_id is
 * product-level, but the back shape and back variant are per variant, so
 * after a save that ADDS a back the operator (or whoever can) checks them on
 * every variant in Shopify. Rendered inside the dialog's existing content (one
 * dialog root, so the backdrop never remounts); the heading takes focus when
 * the step appears; the action line is an alert.
 */
function VariantSetupStep({ setup, route, onContinue }: {
  setup: VariantSetup;
  route: IssueProductRoute;
  onContinue: () => void;
}) {
  const { t } = useTranslation('production');
  const { organization } = useOrganization();
  const titleRef = useRef<HTMLHeadingElement>(null);
  // The content stays mounted across the swap, so Radix's open auto-focus
  // does not run again: move focus to the new heading here.
  useEffect(() => { titleRef.current?.focus(); }, []);
  const productHref = route.kind === 'admin'
    ? getShopifyAdminProductUrl(organization?.shopify_domain, setup.productId)
    : null;
  return (
    <>
      <DialogHeader>
        <DialogTitle ref={titleRef} tabIndex={-1} className="focus:outline-none">
          {t('issues.printFiles.fixDesign.variantsTitle')}
        </DialogTitle>
        <DialogDescription className="text-base">{t('issues.printFiles.fixDesign.variantsDescription')}</DialogDescription>
      </DialogHeader>
      <div
        role="alert"
        className="space-y-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-base text-amber-900 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-100"
      >
        <p>
          {route.kind === 'askClient'
            ? t('issues.printFiles.fixDesign.variantsAskClient', { client: route.clientName })
            : route.kind === 'askAdmin'
              ? t('issues.printFiles.fixDesign.variantsAskAdmin')
              : t('issues.printFiles.fixDesign.variantsOpenProduct')}
        </p>
        {productHref && (
          <Button asChild variant="outline" className="h-12 gap-1.5 bg-background text-base text-foreground">
            <a href={productHref} target="_blank" rel="noopener noreferrer">
              {t('issues.printFiles.fixDesign.openInShopify')}
              <ExternalLink className="h-4 w-4" aria-hidden="true" />
              <span className="sr-only">{t('common:opensInNewTab')}</span>
            </a>
          </Button>
        )}
      </div>
      <DialogFooter>
        <Button
          className="h-12 gap-1.5 bg-threadheads-purple text-base text-white hover:bg-threadheads-purple/90"
          onClick={onContinue}
        >
          {t('issues.printFiles.fixDesign.variantsContinue')}
          <ArrowRight className="h-5 w-5" aria-hidden="true" />
        </Button>
      </DialogFooter>
    </>
  );
}

export function ResolveDialog({
  open,
  onOpenChange,
  issueId,
  locationId,
  orderName,
  issueType,
  metadata,
  bridgeFetchState,
  bridgeFetchStateAt,
  bridgeFetchFailReason,
  blockedSku,
  shopifyProductId,
  issueOrganizationId,
  productRoute = { kind: 'askAdmin' },
  onSuccess,
}: ResolveDialogProps) {
  const { t } = useTranslation('production');
  const isOosType = issueType === 'INVENTORY_ERROR' || issueType === 'OOS_PRE_BATCH';

  // "Upload the file instead" (the isCustomNoArtwork branch below) converts
  // the issue server-side, then swaps the dialog in place to the uploader
  // branch — no close-and-reopen, no refetch needed. `effectiveMetadata`
  // overlays the conversion's result onto the (possibly stale) `metadata`
  // prop so the branch flip is immediate; the real refetch (triggered below)
  // catches up the row underneath.
  const [metadataReady, setMetadataReady] = useState(false);
  const [metadataUploadIntent, setMetadataUploadIntent] = useState(false);
  // BLU-1287: the variant set-up step after a Fix design data save that added a back.
  const [variantSetup, setVariantSetup] = useState<VariantSetup | null>(null);
  const [convertedFilename, setConvertedFilename] = useState<string | null>(null);
  const effectiveMetadata = convertedFilename
    ? { ...metadata, reason: 'file_missing', kind: 'ondemand', filename: convertedFilename }
    : metadata;

  // Self-heal case: a missing print file. Detected purely from issue shape so
  // the rest of the dialog is untouched for every other issue type.
  const reason = effectiveMetadata?.reason as string | undefined;
  const isFileMissing = issueType === 'PRINT_FILE_ISSUE' && reason === 'file_missing';
  // Feature B: a dual product with no print_file_lookups row — needs front/back
  // print data, not a file upload.
  const isPrintDataNeeded = issueType === 'PRINT_FILE_ISSUE' && reason === 'print_data_needed';
  // Wrong-data print-file issue (wrong_design etc.) — the fix-design-data form,
  // with a manual-resolve escape hatch for issues fixed out-of-band.
  const [manualChosen, setManualOverride] = useState(false);
  // A broken shared-blank link is fixed at the link, never with a file or a
  // design-data edit (canUploadReplacementForPrintFileReason refuses it and the
  // server refuses every correction): only mark resolved or put on hold.
  const blankLinkBroken =
    issueType === 'PRINT_FILE_ISSUE' && reason === SUPPLY_MAPPING_INVALID_PRINT_FILE_REASON;
  const manualOverride = manualChosen || blankLinkBroken;
  // Within the wrong-data branch, switch to the "upload a corrected file" flow
  // (the ONLY path for wrong_position / customs; an alternative for the rest).
  const [wrongDataUpload, setWrongDataUpload] = useState(false);
  const isWrongData =
    issueType === 'PRINT_FILE_ISSUE' &&
    typeof reason === 'string' &&
    WRONG_DATA_REASONS.has(reason) &&
    !manualOverride;
  const isCustomSku = typeof blockedSku === 'string' && /^(CUST|PW)/i.test(blockedSku);
  // Legacy custom with NO design reference (missing_artwork shape, no filename):
  // the only fix is attaching artwork — offer the paste-a-URL heal.
  const isCustomNoArtwork =
    issueType === 'PRINT_FILE_ISSUE' && reason === 'missing_artwork' && isCustomSku && !manualOverride;
  // Default the kind from the recorded metadata.kind (the worker stamps it).
  const defaultKind = effectiveMetadata?.kind === 'ondemand' ? 'ondemand' : 'regular';

  // Scrolls the revealed upload section into view: on a metadata issue the
  // per-side uploader sits BELOW a two-column design-data form, so revealing
  // it without moving the viewport looked like the button did nothing.
  const revealUploadSection = useCallback((node: HTMLElement | null) => {
    if (typeof node?.scrollIntoView !== 'function') return;
    const reduced =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    node.scrollIntoView({ block: 'nearest', behavior: reduced ? 'auto' : 'smooth' });
  }, []);

  const [method, setMethod] = useState<'manual' | 'cancelled'>('manual');
  const [notes, setNotes] = useState('');
  const [isRechecking, setIsRechecking] = useState(false);
  const [recheckMessage, setRecheckMessage] = useState<string | null>(null);
  // Tone for the inline re-check line: red ONLY when the call itself failed.
  // "still fetching" / "needs a corrected upload" are states, not errors.
  const [recheckFailed, setRecheckFailed] = useState(false);
  // In-flight guard for the paste-a-URL heal (ArtworkUrlSection, isCustomNoArtwork
  // branch) — disables "Upload the file instead" while a heal is running.
  const [isUrlHealing, setIsUrlHealing] = useState(false);
  // BLU-880: a bridge re-fetch being tracked live by ArtworkUrlSection. While
  // in flight the manual upload zone is hidden — an upload would race the RIP.
  const [isBridgeFetchInFlight, setIsBridgeFetchInFlight] = useState(false);
  // A run already in flight when the dialog opened (the row's TYPED stamp
  // projection) — the section seeds its stepper from it so reopening never
  // loses progress.
  const initialBridgeFetch = snapshotFromFields(
    bridgeFetchState,
    bridgeFetchStateAt,
    bridgeFetchFailReason,
  );

  const resolveIssue = useResolveIssue();
  const utils = trpc.useUtils();
  const { recheckOne } = usePrintFileIssueRecheck();
  // Legacy custom, no artwork: convert to a normal file_missing row so the
  // operator can upload the file locally instead of pasting a URL.
  const convertToUploadMutation = trpc.printAgentAdmin.convertCustomIssueToUploadable.useMutation();
  // BLU-651 A3: NON-cascading manual resolve for wrong-data issues — the
  // generic resolve cascades to every OPEN sibling on the prod_order and
  // fires "Ready to Reprint" messaging for artwork that was never fixed.
  const resolveWrongDataMutation = trpc.printAgentAdmin.resolveWrongDataManually.useMutation();
  // The wrong-data class is detected from the ORIGINAL reason (not
  // effectiveMetadata) so the manual escape hatch still routes to the narrow
  // resolve after any in-dialog conversion overlay.
  const isWrongDataReason =
    issueType === 'PRINT_FILE_ISSUE' &&
    typeof metadata?.reason === 'string' &&
    WRONG_DATA_REASONS.has(metadata.reason);

  // Reset state when dialog closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setMethod('manual');
      setNotes('');
      setRecheckMessage(null);
      setRecheckFailed(false);
      setManualOverride(false);
      setWrongDataUpload(false);
      setConvertedFilename(null);
      setMetadataReady(false);
      setMetadataUploadIntent(false);
      setVariantSetup(null);
      setIsUrlHealing(false);
      setIsBridgeFetchInFlight(false);
    }
    onOpenChange(newOpen);
  };

  const handleConvertToUpload = async () => {
    if (convertToUploadMutation.isPending) return;
    try {
      const result = await convertToUploadMutation.mutateAsync({ issueId });
      toast.success(t('issues.printFiles.artworkUrl.convertSuccess'));
      utils.productionOrders.issues.list.invalidate();
      utils.productionOrders.issues.getCounts.invalidate();
      // Keep the dialog open and swap it to the uploader — no close/reopen.
      setConvertedFilename(result.filename);
    } catch (error) {
      toast.error(getUserMessage(error));
    }
  };

  // Re-check the cloud for an existing file_missing issue: present on active →
  // resolve; recovered from backup → heal + resolve; else still missing.
  const handleRecheck = async () => {
    if (isRechecking) return;
    setIsRechecking(true);
    setRecheckMessage(null);
    setRecheckFailed(false);
    try {
      const { status } = await recheckOne(issueId);
      // Disposition rule: TERMINAL statuses (resolved/healed) close the
      // dialog with a success toast; `requeued` also closes — the bridge now
      // owns the retry, there's nothing left for this dialog to track.
      // The NON-terminal, SELF-resolving status (`healing` — still actively
      // fetching/writing, not yet confirmed anywhere) keeps the dialog open
      // with a persistent inline status message instead — the operator can
      // re-check again, or the server's own background heal resolves it
      // without further input.
      if (status === 'resolved' || status === 'healed') {
        toast.success(
          status === 'healed'
            ? t('issues.printFiles.recheck.healed')
            : t('issues.printFiles.recheck.resolved')
        );
        utils.productionOrders.issues.list.invalidate();
        handleOpenChange(false);
        onSuccess?.();
      } else if (status === 'requeued') {
        setRecheckMessage(t('issues.printFiles.selfHealRequeued'));
        void utils.productionOrders.issues.list.invalidate();
        void utils.printAgentAdmin.getPrintFileIssueTargets.invalidate({ issueId });
      } else if (status === 'metadata_mismatch') {
        // Re-check may have just reclassified a legacy file_missing row after
        // finding that its SKU and lookup design tokens disagree. Refresh and
        // close so the card immediately changes to Metadata Mismatch and shows
        // the new operator guide instead of leaving a stale upload dialog open.
        toast.info(t('issues.printFiles.recheck.metadataMismatchDetected'));
        utils.productionOrders.issues.list.invalidate();
        utils.productionOrders.issues.getCounts.invalidate();
        handleOpenChange(false);
        onSuccess?.();
      } else {
        // Still missing / still healing / stalled on a metadata write — keep
        // the dialog open. The inline message (role=status, persistent) is the
        // durable feedback; a toast would be redundant for a stay-in-dialog
        // flow. Every non-terminal status the router can return needs its OWN
        // line here: falling back to "still missing" for a wrong-artwork row
        // or a Shopify sync stall tells the operator to do the one thing that
        // cannot work.
        setRecheckMessage(
          status === 'healing'
            ? t('issues.printFiles.recheck.healing')
            : status === 'metadata_sync_failed'
              ? t('issues.printFiles.recheck.metadataSyncFailed')
              : status === 'wrong_data_needs_upload'
                ? t('issues.printFiles.recheck.wrongDataNeedsUpload')
                : status === 'platen_variant_file_needed'
                  ? t('issues.printFiles.recheck.platenVariantFileNeeded')
                  : t('issues.printFiles.recheck.stillMissing'),
        );
      }
    } catch (error) {
      const message = getUserMessage(error);
      setRecheckMessage(message);
      setRecheckFailed(true);
      toast.error(message);
    } finally {
      setIsRechecking(false);
    }
  };

  const handleResolve = async () => {
    try {
      if (isWrongDataReason && method === 'manual') {
        // BLU-651 A3: wrong-data "handled out-of-band" goes through the NARROW
        // resolve — only this one issue flips (a co-located OOS/REDO sibling
        // stays OPEN), resolution_method 'manual_wrong_data' (no "Ready to
        // Reprint" Slack/bell, no retry-feed row). "Put on hold" keeps the
        // generic path — holding is an order-level action by design.
        await resolveWrongDataMutation.mutateAsync({
          issueId,
          notes: notes || undefined,
        });
        utils.productionOrders.issues.list.invalidate();
        utils.productionOrders.issues.getCounts.invalidate();
        toast.success(t('issues.printFiles.fixDesign.manualResolved'));
        handleOpenChange(false);
        onSuccess?.();
        return;
      }
      await resolveIssue.mutateAsync({
        issue_id: issueId,
        resolution_method: method,
        notes: notes || undefined,
        // "Put on hold" → a manual hold (operator sends it back from the On hold list).
        ...(method === 'cancelled' ? { hold_reason: 'manual' as const } : {}),
      });

      toast.success(
        method === 'cancelled'
          ? t('issues.resolve.orderHeld')
          : isOosType
            ? t('issues.resolve.resolvedRequeued')
            : t('issues.resolve.issueResolved')
      );
      handleOpenChange(false);
      onSuccess?.();
    } catch (error) {
      const message = error instanceof Error ? error.message : t('issues.resolve.failed');
      toast.error(message);
    }
  };

  const handleMetadataSaved = () => {
    void utils.productionOrders.issues.list.invalidate();
    void utils.productionOrders.issues.getCounts.invalidate();
    void utils.printAgentAdmin.getPrintFileIssueTargets.invalidate({ issueId });
    setMetadataReady(true);
  };
  // Fix design data resolved the issue: refresh the list and close.
  const handleFixDesignResolved = () => {
    utils.productionOrders.issues.list.invalidate();
    handleOpenChange(false);
    onSuccess?.();
  };
  // BLU-1287: Continue at the variant set-up step does what the form would
  // have done without the step.
  const handleVariantSetupContinue = () => {
    const next = variantSetup?.next;
    setVariantSetup(null);
    if (next === 'upload') handleMetadataSaved();
    else if (next === 'resolved') handleFixDesignResolved();
  };
  // Closing the step (X, Esc, outside click): on the resolved path it equals
  // Continue. On the save-for-upload path it closes the dialog and refreshes
  // the list (the issue stays open, its metadata saved); only Continue goes
  // on to the upload step.
  const handleVariantSetupDismiss = () => {
    if (variantSetup?.next === 'resolved') {
      handleVariantSetupContinue();
      return;
    }
    void utils.productionOrders.issues.list.invalidate();
    void utils.productionOrders.issues.getCounts.invalidate();
    handleOpenChange(false);
  };
  // The same Dialog and DialogContent elements as every other branch, so
  // only the content swaps and the backdrop never remounts (no blink).
  if (variantSetup) return <Dialog open={open} onOpenChange={(next) => { if (!next) handleVariantSetupDismiss(); }}>
    <DialogContent className={DESIGN_DATA_DIALOG_CLASS}>
      <VariantSetupStep setup={variantSetup} route={productRoute} onContinue={handleVariantSetupContinue} />
    </DialogContent>
  </Dialog>;
  if (metadataReady) return <Dialog open={open} onOpenChange={handleOpenChange}>
    <DialogContent className={DESIGN_DATA_DIALOG_CLASS}>
      <DialogHeader><DialogTitle>{t('issues.printFiles.upload.title')}</DialogTitle>
        <DialogDescription>{t('issues.printFiles.upload.metadataSaved')}</DialogDescription></DialogHeader>
      <PrintFileSelfHealForm issueId={issueId} shopifyProductId={shopifyProductId} issueOrganizationId={issueOrganizationId} onResolved={() => { handleOpenChange(false); onSuccess?.(); }} onCancel={() => handleOpenChange(false)} />
    </DialogContent>
  </Dialog>;

  // ── Feature B: dual product needs front/back print data ─────────────────────
  if (isPrintDataNeeded) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className={DESIGN_DATA_DIALOG_CLASS}>
          <DialogHeader>
            <DialogTitle>{t('issues.printFiles.printData.title')}</DialogTitle>
            <DialogDescription>
              {orderName
                ? t('issues.printFiles.printData.descriptionWithOrder', { orderName })
                : t('issues.printFiles.printData.description')}
            </DialogDescription>
          </DialogHeader>

          {!metadataUploadIntent && (
            <Button
              className="h-12 text-base"
              variant="outline"
              onClick={() => setMetadataUploadIntent(true)}
            >
              {t('issues.printFiles.upload.uploadArtwork')}
            </Button>
          )}
          <PrintDataForm
            issueId={issueId}
            blockedSku={blockedSku ?? null}
            onSavedForUpload={handleMetadataSaved}
            onResolved={() => {
              handleOpenChange(false);
              onSuccess?.();
            }}
            onCancel={() => handleOpenChange(false)}
          />
          {/* "Upload print files" reveals the real uploader in place — the
              per-side rows, locked, with the reason they're locked ON them.
              Saving the design data above unlocks it without a second click. */}
          {metadataUploadIntent && (
            <div ref={revealUploadSection} className="border-t pt-3">
              <PrintFileSelfHealForm issueId={issueId} shopifyProductId={shopifyProductId} issueOrganizationId={issueOrganizationId} embedded onResolved={() => { handleOpenChange(false); onSuccess?.(); }} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  // ── Wrong data: correct the design data, OR upload a corrected file ──────────
  if (isWrongData) {
    const needsMetadata = reason === 'metadata_mismatch' && !(effectiveMetadata?.metadata_correction as { synchronized?: boolean } | undefined)?.synchronized;
    const uploadOnly = isCustomSku || reason === 'wrong_position';
    const showUpload = !needsMetadata && (uploadOnly || wrongDataUpload);
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className={DESIGN_DATA_DIALOG_CLASS}>
          <DialogHeader>
            <DialogTitle>
              {showUpload
                ? t('issues.printFiles.wrongDataUpload.title')
                : t('issues.printFiles.fixDesign.title')}
            </DialogTitle>
            <DialogDescription>
              {showUpload
                ? orderName
                  ? t('issues.printFiles.wrongDataUpload.descriptionWithOrder', { orderName })
                  : t('issues.printFiles.wrongDataUpload.description')
                : orderName
                  ? t('issues.printFiles.fixDesign.descriptionWithOrder', { orderName })
                  : t('issues.printFiles.fixDesign.description')}
            </DialogDescription>
          </DialogHeader>

          {showUpload ? (
            <>
              <WrongDataUploadForm
                issueId={issueId}
                blockedSku={blockedSku ?? null}
                shopifyProductId={shopifyProductId}
                issueOrganizationId={issueOrganizationId}
                onResolved={() => {
                  utils.productionOrders.issues.list.invalidate();
                  utils.productionOrders.issues.getCounts.invalidate();
                  handleOpenChange(false);
                  onSuccess?.();
                }}
                onCancel={() => handleOpenChange(false)}
              />
              {/* Switched here from the token form → allow going back (only when
                  the token form is a valid alternative for this reason/SKU). */}
              {!uploadOnly && (
                <button
                  type="button"
                  className="mx-auto min-h-12 px-3 text-base text-muted-foreground underline"
                  onClick={() => setWrongDataUpload(false)}
                >
                  {t('issues.printFiles.upload.updateMetadata')}
                </button>
              )}
            </>
          ) : (
            <>
              <FixDesignDataForm
                onSavedForUpload={handleMetadataSaved}
                issueId={issueId}
                blockedSku={blockedSku ?? null}
                onResolved={handleFixDesignResolved}
                onVariantSetup={(next, productId) => setVariantSetup({ next, productId })}
                onCancel={() => handleOpenChange(false)}
              />
              {/* Alternative: the correct file isn't on the cloud yet — upload
                  it. On a metadata mismatch the uploader stays locked until
                  the design data above is saved, but it is REVEALED (with the
                  reason on the rows) rather than replaced by a paragraph. */}
              {!wrongDataUpload ? (
                <button
                  type="button"
                  className="mx-auto min-h-12 px-3 text-base text-muted-foreground underline"
                  onClick={() => setWrongDataUpload(true)}
                >
                  {t('issues.printFiles.upload.uploadArtwork')}
                </button>
              ) : (
                <div ref={revealUploadSection} className="border-t pt-3">
                  <PrintFileSelfHealForm
                    issueId={issueId}
                    shopifyProductId={shopifyProductId} issueOrganizationId={issueOrganizationId}
                    embedded
                    resolvedToastKey="issues.printFiles.wrongDataUpload.resolved"
                    onResolved={() => {
                      utils.productionOrders.issues.list.invalidate();
                      utils.productionOrders.issues.getCounts.invalidate();
                      handleOpenChange(false);
                      onSuccess?.();
                    }}
                  />
                </div>
              )}
            </>
          )}

          {/* Escape hatch: the fix happened out-of-band — plain manual resolve. */}
          <button
            type="button"
            className="mx-auto min-h-12 px-3 text-base text-muted-foreground underline"
            onClick={() => setManualOverride(true)}
          >
            {t('issues.printFiles.fixDesign.manualInstead')}
          </button>
        </DialogContent>
      </Dialog>
    );
  }

  // ── Legacy custom with no design reference: attach artwork by URL ────────────
  if (isCustomNoArtwork) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>{t('issues.printFiles.artworkUrl.dialogTitle')}</DialogTitle>
            <DialogDescription>
              {orderName
                ? t('issues.printFiles.artworkUrl.dialogDescriptionWithOrder', { orderName })
                : t('issues.printFiles.artworkUrl.dialogDescription')}
            </DialogDescription>
          </DialogHeader>

          {blockedSku && (
            <p className="text-xs text-muted-foreground">
              {t('issues.printFiles.printData.skuContext')}{' '}
              <span className="font-mono text-foreground">{blockedSku}</span>
            </p>
          )}

          <ArtworkUrlSection
            issueId={issueId}
            locationId={locationId}
            disabled={convertToUploadMutation.isPending}
            initialBridgeFetch={initialBridgeFetch}
            onHealingChange={setIsUrlHealing}
            onBridgeFetchChange={setIsBridgeFetchInFlight}
            onResolved={() => {
              handleOpenChange(false);
              onSuccess?.();
            }}
          />

          {/* Alternative to the paste-URL flow above: the operator has the
              file locally instead of a link. Converts the row to a normal
              file_missing issue with the standard Upload buttons. */}
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <div className="h-px flex-1 bg-border" aria-hidden="true" />
            {t('issues.printFiles.artworkUrl.or')}
            <div className="h-px flex-1 bg-border" aria-hidden="true" />
          </div>

          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-12 w-full gap-1.5"
            onClick={handleConvertToUpload}
            disabled={convertToUploadMutation.isPending || isUrlHealing || isBridgeFetchInFlight}
          >
            {convertToUploadMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Upload className="h-4 w-4" aria-hidden="true" />
            )}
            {t('issues.printFiles.upload.uploadArtwork')}
          </Button>

          {/* Escape hatch: handled out-of-band — plain manual resolve. */}
          <button
            type="button"
            className="mx-auto min-h-12 px-3 text-base text-muted-foreground underline"
            onClick={() => setManualOverride(true)}
          >
            {t('issues.printFiles.artworkUrl.manualInstead')}
          </button>
        </DialogContent>
      </Dialog>
    );
  }

  // ── Self-heal: missing print file ──────────────────────────────────────────
  // Catch-all for every print-file issue the branches above didn't claim —
  // including a NON-custom `missing_artwork` (nothing to upload) and any row
  // whose printer fleet can't be resolved. `manualOverride` gates it so those
  // rows keep an escape instead of dead-ending on an uploader that can't help.
  if (!manualOverride && (isFileMissing || issueType === 'PRINT_FILE_ISSUE')) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        {/* Re-check + URL section + live stepper + upload form stack past a
            short viewport — scroll inside (HoldBackConfirmDialog pattern). */}
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>{t('issues.printFiles.upload.title')}</DialogTitle>
            <DialogDescription>
              {orderName
                ? t('issues.printFiles.upload.descriptionWithOrder', { orderName })
                : t('issues.printFiles.upload.description')}
            </DialogDescription>
          </DialogHeader>

          {/* Re-check the cloud first — handles the case where the file was
              already uploaded out-of-band (or sits on the backup cloud). */}
          <div className="rounded-lg border border-dashed border-threadheads-purple/30 bg-threadheads-purple/5 p-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-base text-muted-foreground">
                {t('issues.printFiles.recheck.hint')}
              </p>
              <Button
                type="button"
                variant="outline"
                className="h-12 shrink-0 gap-1.5 text-base"
                onClick={handleRecheck}
                disabled={isRechecking}
              >
                {isRechecking ? (
                  <Loader2 className="h-5 w-5 motion-safe:animate-spin" aria-hidden="true" />
                ) : (
                  <RefreshCw className="h-5 w-5" aria-hidden="true" />
                )}
                {t('issues.printFiles.recheck.button')}
              </Button>
            </div>
            {recheckMessage && (
              <p
                className={`mt-2 text-base ${recheckFailed ? 'text-destructive' : 'text-amber-800 dark:text-amber-200'}`}
                role="status"
                aria-live="polite"
              >
                {recheckMessage}
              </p>
            )}
          </div>

          {/* Customs: the artwork can also be fetched server-side from a pasted
              storage URL (Drive/GCS/S3) — no local file needed. */}
          {defaultKind === 'ondemand' && isCustomSku && (
            <ArtworkUrlSection
              issueId={issueId}
              locationId={locationId}
              initialBridgeFetch={initialBridgeFetch}
              onBridgeFetchChange={setIsBridgeFetchInFlight}
              onResolved={() => {
                handleOpenChange(false);
                onSuccess?.();
              }}
            />
          )}

          {/* Per-target locking lives in the form itself (the server marks an
              output the bridge currently owns as `locked`) — a manual upload
              must not race the RIP for the same key. The form stays mounted
              either way: it owns the dialog's only Cancel. */}
          <PrintFileSelfHealForm
            issueId={issueId}
            shopifyProductId={shopifyProductId} issueOrganizationId={issueOrganizationId}
            onResolved={() => {
              handleOpenChange(false);
              onSuccess?.();
            }}
            onCancel={() => handleOpenChange(false)}
          />

          {/* Escape hatch: nothing here can fix this row (no artwork exists to
              upload, or the printer fleet is unknown) — fall through to the
              manual-resolve / put-on-hold dialog. */}
          <button
            type="button"
            className="mx-auto min-h-12 px-3 text-base text-muted-foreground underline"
            onClick={() => setManualOverride(true)}
          >
            {t('issues.printFiles.fixDesign.manualInstead')}
          </button>
        </DialogContent>
      </Dialog>
    );
  }

  // ── Default: manual resolve / put-on-hold ───────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t('issues.resolve.title')}</DialogTitle>
          <DialogDescription>
            {orderName
              ? t('issues.resolve.descriptionWithOrder', { orderName })
              : t('issues.resolve.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          <RadioGroup
            value={method}
            onValueChange={(v) => setMethod(v as 'manual' | 'cancelled')}
            className="space-y-3"
          >
            <div className="flex items-center space-x-3 rounded-lg border p-4 min-h-16 hover:bg-muted/50">
              <RadioGroupItem value="manual" id="manual" />
              <Label htmlFor="manual" className="flex items-center gap-2 cursor-pointer">
                <CheckCircle className="h-5 w-5 text-green-600" aria-hidden="true" />
                <div>
                  <p className="font-medium">{t('issues.resolve.markResolved')}</p>
                  <p className="text-sm text-muted-foreground">
                    {isOosType
                      ? t('issues.resolve.stockConfirmed')
                      : t('issues.resolve.manuallyAddressed')}
                  </p>
                </div>
              </Label>
            </div>

            <div className="flex items-center space-x-3 rounded-lg border p-4 min-h-16 hover:bg-muted/50">
              <RadioGroupItem value="cancelled" id="cancelled" />
              <Label htmlFor="cancelled" className="flex items-center gap-2 cursor-pointer">
                <Pause className="h-5 w-5 text-amber-500" aria-hidden="true" />
                <div>
                  <p className="font-medium">{t('issues.resolve.putOnHold')}</p>
                  <p className="text-sm text-muted-foreground">
                    {t('issues.resolve.putOnHoldDesc')}
                  </p>
                </div>
              </Label>
            </div>
          </RadioGroup>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">{t('issues.resolve.notesLabel')}</Label>
            <Textarea
              id="notes"
              placeholder={t('issues.resolve.notesPlaceholder')}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={500}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            {t('issues.resolve.cancelBtn')}
          </Button>
          <Button
            onClick={handleResolve}
            disabled={resolveIssue.isPending || resolveWrongDataMutation.isPending}
            className={`h-12 min-w-[100px] ${method === 'cancelled' ? 'bg-amber-500 text-white hover:bg-amber-600' : ''}`}
          >
            {resolveIssue.isPending || resolveWrongDataMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : method === 'cancelled' ? (
              t('issues.resolve.putOnHold')
            ) : (
              t('issues.resolve')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
